/**
 * services/lead-notes.js — internal notes timeline per (contact, channel).
 *
 * #167. Wraps the proxy /lead-notes routes (GET / POST / DELETE) and exposes
 * a reactive store keyed by `${contactId}:${channelPhoneId}` so multiple
 * UI surfaces (NotesSheet, future contact-panel preview) can subscribe to
 * the same entry without re-fetching.
 *
 * Design notes:
 *
 * - **No realtime subscription.** Notes are low-frequency by nature (an
 *   agent writes one every few minutes at most) and they're internal —
 *   if Alice's phone shows a slightly stale list until the sheet is
 *   re-opened, that's fine. We refresh on:
 *     · sheet open (always)
 *     · after a successful create/delete (optimistic + reload)
 *     · on transfer success (so the auto-written 'transfer' row shows up
 *       without the user having to manually refresh)
 *
 * - **Per-key entry shape.** Each map key holds
 *   `{ rows, loadedAt, loading, error }` so the UI can show a spinner
 *   on first load AND distinguish "haven't asked yet" (no entry) from
 *   "asked, got back 0 rows". Without `loadedAt` an empty array would
 *   look identical to "still loading".
 *
 * - **Auto-rows aren't returned by createNote.** The /lead-notes POST
 *   route only creates kind='manual'; auto-rows for transfer/assigned/
 *   done are written server-side as a side effect of /chats/:id/assign
 *   and /chats/:id/done. To pick those up, callers fire `loadNotes()`
 *   AFTER the action completes (Thread.js does this for transfers).
 */

import { atom, map } from 'nanostores';
import * as api from './api.js';

const _key = (contactId, channelPhoneId) => `${contactId}:${channelPhoneId}`;

/** Per-key store: each entry `{ rows, loadedAt, loading, error }`. */
export const $notesByKey = map({});

function _setEntry(key, patch) {
  const cur = $notesByKey.get()[key] || { rows: [], loadedAt: 0, loading: false, error: null };
  $notesByKey.setKey(key, { ...cur, ...patch });
}

/**
 * Load (or refresh) the timeline for a (contact, channel). Sets
 * `loading: true` while the fetch is in flight so the sheet can render
 * a skeleton state on first open.
 *
 * Resolves with the fresh array of rows. Rejects only on network error
 * — server returning [] is success.
 */
export async function loadNotes(contactId, channelPhoneId) {
  if (!contactId || !channelPhoneId) return [];
  const key = _key(contactId, channelPhoneId);
  _setEntry(key, { loading: true, error: null });
  try {
    const res = await api.fetchLeadNotes(contactId, channelPhoneId);
    const rows = Array.isArray(res?.data) ? res.data : [];
    _setEntry(key, { rows, loadedAt: Date.now(), loading: false });
    return rows;
  } catch (e) {
    console.warn('[lead-notes] load failed:', e?.message);
    _setEntry(key, { loading: false, error: e?.message || 'Failed to load notes' });
    throw e;
  }
}

/**
 * Create a manual note. Optimistically prepends a placeholder row so the
 * sheet feels instant, then replaces it with the server-confirmed row on
 * success. On failure removes the placeholder and rethrows so the caller
 * can show a toast.
 */
export async function createNote({ contactId, channelPhoneId, body, authorEmail, authorName }) {
  if (!contactId || !channelPhoneId) throw new Error('contactId and channelPhoneId required');
  const text = String(body || '').trim();
  if (!text) throw new Error('Note cannot be empty');
  if (text.length > 500) throw new Error('Note must be 500 chars or fewer');

  const key = _key(contactId, channelPhoneId);
  const tempId = `__temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const optimistic = {
    id:                  tempId,
    author_email:        authorEmail || null,
    author_name:         authorName  || null,
    body:                text,
    kind:                'manual',
    transfer_from_email: null,
    transfer_to_email:   null,
    created_at:          new Date().toISOString(),
    _pending:            true,
  };
  const cur = $notesByKey.get()[key] || { rows: [], loadedAt: 0, loading: false, error: null };
  $notesByKey.setKey(key, { ...cur, rows: [optimistic, ...cur.rows] });

  try {
    const res = await api.createLeadNote({ contactId, channelPhoneId, body: text });
    const persisted = res?.data || null;
    // Replace the optimistic row in place — keeps scroll position stable.
    const after = $notesByKey.get()[key] || cur;
    const swapped = after.rows.map(r => r.id === tempId ? (persisted || { ...optimistic, _pending: false }) : r);
    $notesByKey.setKey(key, { ...after, rows: swapped });
    return persisted;
  } catch (e) {
    // Roll back the optimistic insert.
    const after = $notesByKey.get()[key] || cur;
    $notesByKey.setKey(key, { ...after, rows: after.rows.filter(r => r.id !== tempId) });
    throw e;
  }
}

/**
 * Soft-delete a note. Server enforces "author only" — non-author DELETE
 * gets a 404 which we surface as a generic "couldn't delete" error.
 *
 * Optimistically removes the row from the store, restores it on failure.
 */
export async function deleteNote(id, contactId, channelPhoneId) {
  if (!id || !contactId || !channelPhoneId) throw new Error('id + key required');
  const key = _key(contactId, channelPhoneId);
  const cur = $notesByKey.get()[key];
  if (!cur) return; // never loaded — nothing to optimistically remove
  const removed = cur.rows.find(r => r.id === id);
  $notesByKey.setKey(key, { ...cur, rows: cur.rows.filter(r => r.id !== id) });
  try {
    await api.deleteLeadNote(id);
  } catch (e) {
    if (removed) {
      const after = $notesByKey.get()[key] || cur;
      // Re-insert at original index by re-loading from server — easier
      // than tracking the original position across other concurrent edits.
      loadNotes(contactId, channelPhoneId).catch(() => {});
    }
    throw e;
  }
}
